import { useState, useCallback } from 'react'

type Draft<Base, Patch extends Partial<Base>> = {
  loading: boolean
  setLoading: (loading: boolean) => void
  base: Base | undefined
  setBase: (base: Base | undefined) => void
  values: (Base & Partial<Patch>) | Partial<Patch>
  changes: Partial<Patch>
  handleChange: <K extends keyof Patch>(key: K, value: Patch[K]) => void
  hasChanges: boolean
  reset: () => void
}

export function useDraft<Base, Patch extends Partial<Base>>(
  init?: Base,
): Draft<Base, Patch> {
  const [loading, setLoading] = useState(false)
  const [internalBase, setInternalBase] = useState<Base>()
  const [changes, setChanges] = useState<Partial<Patch>>({})

  // With init: base follows init, updates automatically if init changes
  // Without init: base is managed internally, updates via setBase
  const base = init !== undefined ? init : internalBase

  // useCallback since this is often passed as a prop. Without it, state updates to
  // `changes` recreate this function, which then causes unnecessary child re-renders.
  const handleChange = useCallback(
    <K extends keyof Patch>(key: K, value: Patch[K]): void => {
      setChanges((prev) => ({ ...prev, [key]: value }))
    },
    [],
  )

  const reset = useCallback((): void => setChanges({}), [])

  const values = { ...base, ...changes }

  const baseRecord: Record<string, unknown> = { ...base }
  const changesRecord: Record<string, unknown> = { ...changes }
  const hasChanges = Object.keys(changesRecord).some(
    (key) => changesRecord[key] !== baseRecord[key],
  )

  return {
    loading,
    setLoading,
    base,
    setBase: setInternalBase,
    values,
    changes,
    handleChange,
    hasChanges,
    reset,
  }
}
